import { AimsCommonPopupEditorComponent } from '@aims/common-components';
import { Overlay } from '@angular/cdk/overlay';
import { isPlatformBrowser, Location } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, Input, isDevMode, OnDestroy, PLATFORM_ID, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment-mini';
import { BehaviorSubject, combineLatest, fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, distinct, distinctUntilChanged, map, take, takeUntil, tap } from 'rxjs/operators';
import { AimsHub, HubParam } from '../../../../../src/app/components/aimsHub/aimsHub';
import { SchMinPermPos, SchScheduleItem } from '../../../../../src/models';
import { TransferHttpService } from '../../../services/transfer-http/transfer-http.service';
import { ScheduleItem } from '../schCommon';
import { AimsScheduleItemEditorComponent } from './aims-schedule-item-editor/aims-schedule-item-editor.component';
import { AimsSchedulerGanttComponent, GanttLine, GanttSchedule } from './aims-scheduler-gantt.component';
import { DayOfMonth, DetailView, GanttDateClick, ScheduledResource, ScheduleFilter, ScheduleItemStatusEnum, ScheduleItemTypeEnum, SchViews } from './aims-scheduler.classes';

@Component({
  selector: 'aims-scheduler',
  styleUrls: ['./aims-scheduler.component.css'],
  templateUrl: './aims-scheduler.component.html',
  providers: [AimsHub]
})
export class AimsSchedulerComponent implements AfterViewInit, OnDestroy {

  constructor(private transferHttp: TransferHttpService
    , private schHub: AimsHub
    , private route: ActivatedRoute
    , private router: Router
    , private location: Location
    ,  @Inject(PLATFORM_ID) private platformId: Object
    , private dialog: MatDialog, private overlay: Overlay) {
  }

  destroy$ = new Subject<boolean>();

  m = moment;

  //Spinner
  spinnerText: string = "Loading Schedule...";
  showSpinner: boolean = false;

  user = this.transferHttp.getCache('user');

  years: Array<number> = [
    2015,
    2016,
    2017,
    2018,
    2019,
    2020,
    2021,
    2022,
    2023,
    2024,
    2025
  ];

  months: Array<object> = [
    { name: 'January', number: 0 },
    { name: 'February', number: 1 },
    { name: 'March', number: 2 },
    { name: 'April', number: 3 },
    { name: 'May', number: 4 },
    { name: 'June', number: 5 },
    { name: 'July', number: 6 },
    { name: 'August', number: 7 },
    { name: 'September', number: 8 },
    { name: 'October', number: 9 },
    { name: 'November', number: 10 },
    { name: 'December', number: 11 }
  ];

  siType = ScheduleItemTypeEnum;

  @ViewChild('nameInput', { static: true }) nameInput: ElementRef;
  @ViewChild('ganntView') ganntView: AimsSchedulerGanttComponent;

  schViews = SchViews;

  showSch: boolean = false;
  showGantt: boolean = false;
  showCalendar: boolean = false;

  siNum: string = null;
  trNum: string = null;

  //Pass to editor
  scheduleItemToEdit: SchScheduleItem;
  isNewSch: boolean;
  companySitesOnly: boolean = true;

  //Default filter
  curSF: ScheduleFilter = new ScheduleFilter(null,
    this.user.defaultSiteId,
    null,
    (this.user.defaultSchedulerView == 'gantt') ? SchViews.month : SchViews.calendar,
    moment()
  );
  scheduleFilter: BehaviorSubject<ScheduleFilter> = new BehaviorSubject(Object.assign({}, this.curSF));

  minPos = this.transferHttp.getSingle<Array<SchMinPermPos>>('Sch/GetMinPermPos').pipe(
    map(mpArr => mpArr.map(m => m.jobTitleId)),
    distinct(mp => mp)
  );

  //yearData is full year's worth, we pull this all at once to improve performance
  schParams: BehaviorSubject<Array<HubParam>> = new BehaviorSubject([new HubParam("year", moment().year()), new HubParam("siteId", this.user.defaultSiteId || 0)]);
  yearData = this.schHub.getHot(`scheduleHub`, this.schParams);
  //data is what gets sent to child components
  prevData: string;
  data: Observable<Array<ScheduledResource>> = combineLatest([this.yearData, this.scheduleFilter, this.minPos]).pipe(
    map(([data, filter, minPosIds]) => {

      //filters selected in dropdowns
      let filteredData = data.map(s => {
        s.start = moment(new Date(s.start));
        s.end = moment(new Date(s.end));
        return s;
      })
        .filter(s =>
          (s.start.isSameOrBefore(filter.dateRange.end) && s.end.isSameOrAfter(filter.dateRange.start))
          && (filter.siteId == null || s.siteId == filter.siteId)
          && (filter.jobTitleId == null || s.jobTitleId == filter.jobTitleId)
          && (filter.name == null || filter.name == '' || s.name.toLowerCase().indexOf(filter.name.toLowerCase()) != -1)
          && (!filter.onlySitePositions || ((s.scheduleItemTypeId == ScheduleItemTypeEnum.rft || s.scheduleItemTypeId == ScheduleItemTypeEnum.vacation) && minPosIds.indexOf(s.jobTitleId) != -1) || s.reliefForId != null)
          && (!filter.onlyNonEmp || s.employeeId == null)
          && (!filter.onlyEmp || s.employeeId != null)
          && (!filter.onlyBilleting || s.billeting)
          && (!filter.onlyTraining || s.scheduleItemTypeId == this.siType.training_emp || s.scheduleItemTypeId == this.siType.training_nonEmp)
          && (!filter.onlyFlights || s.scheduleItemTypeId == this.siType.flight)
          && (!filter.onlyPending || s.scheduleItemStatusId == ScheduleItemStatusEnum.pending)
          && (!filter.onlyOandA || (s.oanumber || s.copaId))
        )
        .sort((s1, s2) => s1.end - s2.end);

      // const fdString = JSON.stringify(data);
      // const notMatching = this.prevData != fdString;
      // if (notMatching || data.length == 0) {
         this.showSch = true;
      //   this.prevData = fdString;
      // }

      return filteredData;
    })
  );

  siteValues = this.transferHttp.getSingle('Sch/GetAllCompanyOrSanSites');
  defaultSite: { siteName: string, siteId: number } = { siteName: 'All', siteId: null };

  jobTitleValues = this.transferHttp.getSingle('Common/GetAllJobTitlesForDropdown');
  defaultJobTitle: { jobTitleId: number, jobTitle: string } = { jobTitleId: null, jobTitle: 'All' };

  year: number = moment().year();

  public handleCompanySitesOnlyChecked() {
    if (this.companySitesOnly) {
      this.siteValues = this.transferHttp.getHot('Sch/GetAllCompanyOrSanSites');
    }
    else {
      this.siteValues = this.transferHttp.getHot('Sch/GetScheduleAndTravelSites');
    }
  }

  //***************************
  // Gantt Related
  //***************************

  ganttDateClick(d: GanttDateClick) {
    this.curSF.selectedView = Object.values(SchViews).filter(v => v.viewType == d.view)[0];
    this.curSF.dateRange = d.dateRange;
    this.pushNext();
  }

  //for Gantt also have to map and send this
  peopleScheduled: Observable<GanttLine[]> = combineLatest([this.data, this.scheduleFilter]).pipe(
    map(([data, filter]) => {
      this.showGantt = false;

      let psMap: Map<string, GanttLine> = new Map();
      data.forEach(sr => {
        const key = `${sr.name}|${sr.jobTitleId}|${sr.resourceId || sr.employeeId}|${sr.scheduleItemStatusId == ScheduleItemStatusEnum.approved}`;
        if (!psMap.has(key)) {
          const scheduledResources = data.filter(sch => key == `${sch.name}|${sch.jobTitleId}|${sch.resourceId || sch.employeeId}|${sch.scheduleItemStatusId == ScheduleItemStatusEnum.approved}`);
          psMap.set(key,
            new GanttLine((sr.employeeId || sr.resourceId),
              sr.name,
              sr.jobTitleId,
              (sr.scheduleItemStatusId == ScheduleItemStatusEnum.approved),
              scheduledResources,
              filter.dateRange.start,
              filter.dateRange.end.endOf('day'))
          );
        }
      });

      let retPsArr: Array<GanttLine> = [...psMap.values()];

      retPsArr.sort((a, b) => {
        if (a.name < b.name) { return -1; }
        if (a.name > b.name) { return 1; }
        return 0;
      });

      this.showGantt = true;

      return retPsArr;
    })
  );

  ganttViews: Array<SchViews> = [
    SchViews.day,
    SchViews.week,
    SchViews.twoWeeks,
    SchViews.month,
    SchViews.threeMonths,
    SchViews.year
  ];
  public numTimeframe: number = 1;

  dateMove(dir: 'forward' | 'back') {
    const filter = this.scheduleFilter.value;
    let diff = {};
    diff['days'] = (dir == 'forward') ? filter.selectedView.duration.days : -filter.selectedView.duration.days;
    this.curSF.updateDateRange(moment(this.curSF.dateRange.start).add(diff));
    this.pushNext();
  }

  setSchItem(schItem: ScheduleItem) {
    this.isNewSch = schItem.createdOn == null;
    if (schItem.createdOn != null) { schItem.createdOn = new Date(schItem.createdOn); };
    if (schItem.arrival != null) { schItem.arrival = new Date(schItem.arrival); };
    if (schItem.departure != null) { schItem.departure = new Date(schItem.departure); };
    this.scheduleItemToEdit = schItem;

    // open dialog
    this.openPopup(schItem);
  }

  public openPopup(schItem: ScheduleItem) {
    this.yearData.pipe(take(1)).subscribe(yd => {
      let yearSched: Array<ScheduledResource> = yd;

      const dialogConfig = new MatDialogConfig();
      dialogConfig.scrollStrategy = this.overlay.scrollStrategies.noop();
      dialogConfig.width = '900px';
      dialogConfig.disableClose = true;
      dialogConfig.data = {
                useRaw: true,
                header: 'Schedule Item Details',
                content: AimsScheduleItemEditorComponent,
                contentData: { itemToEdit: schItem, yearSched: yearSched, }
      };

      const dialogRef = this.dialog.open(AimsCommonPopupEditorComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(dialogResult => {
          if (dialogResult) {
            schItem.scheduleItemTypeId = dialogResult.scheduleItemTypeId;
            schItem.reliefForId = dialogResult.reliefForId;
            schItem.overhireJtId = dialogResult.overhireJtId;

            schItem.siteArrivalNoticeRequired = dialogResult.siteArrivalNoticeRequired;
            schItem.scheduleItemStart = dialogResult.scheduleItemStart;
            schItem.scheduleItemEnd = dialogResult.scheduleItemEnd;
            schItem.siteId = dialogResult.siteId;
            schItem.employeeId = dialogResult.employeeId;
            schItem.resourceId = dialogResult.resourceId;

            schItem.travelArrangeReq = dialogResult.travelArrangeReq;
            schItem.govBilled = dialogResult.govBilled;
            schItem.companyBilled = dialogResult.companyBilled;
            schItem.purpose = dialogResult.purpose;
            schItem.meals = dialogResult.meals;
            schItem.billeting = dialogResult.billeting;
            schItem.notes = dialogResult.notes;
            schItem.oanumber = dialogResult.oanumber;
            schItem.copaId = dialogResult.copanumber;

            schItem.classifiedAccess = dialogResult.classifiedAccess;
            schItem.restrictedAccess = dialogResult.restrictedAccess;
            schItem.comsecaccess = dialogResult.comsecaccess;
            schItem.teraccess = dialogResult.teraccess;
            schItem.escortOnly = dialogResult.escortOnly;
            schItem.restrictedAreaPhotography = dialogResult.restrictedAreaPhotography;
            schItem.radarDowntime = dialogResult.radarDowntime;
            schItem.transToTopCamp = dialogResult.transToTopCamp;
            schItem.firearmRequest = dialogResult.firearmRequest;

            schItem.scheduleItemStatusId = dialogResult.scheduleItemStatusId;
            schItem.carrierId = dialogResult.carrierId;
            schItem.arrivalCarrierSchItemId = dialogResult.arrivalCarrierSchItemId;
            schItem.departureCarrierSchItemId = dialogResult.departureCarrierSchItemId;
            schItem.flightNumber = dialogResult.flightNumber;

            // non-visual
            schItem.jtWsId = dialogResult.jtWsId;
            let addTR: boolean = dialogResult.addTR;

            //Need this for new items or controller doesn't like it
            if (schItem.scheduleItemId == 0) {
              schItem.createdById = this.user.employeeId;
              schItem.createdOn = new Date();
            }

            this.transferHttp.put('Sch/SaveScheduleItem', schItem).pipe(take(1)).subscribe(ret => {
              if (schItem.travelArrangeReq && addTR && isPlatformBrowser(this.platformId)) {
                if (isDevMode()) {
                  window.open(`/scheduling/travelRequest/0/${ret.id}`, "_blank");
                }
                else {
                  window.open(`/scheduling/travelRequest/0/${ret.id}`, "_blank");
                }
              }
              else if (ret.hasOwnProperty("message")) {
                alert(ret.message);
                this.updateSI(null);
              }

              this.updateSI(schItem);
            });
          }
          else {
            this.updateSI(null);
          }
        });
    });
  }


  //***************************
  // Calendar
  //***************************

  daysOfMonth: Observable<DayOfMonth[]> = combineLatest([this.data, this.scheduleFilter]).pipe(
    map(([data, filter]) => {
      this.showCalendar = false;
      //Map to DayOfMonth array
      let retDOMArr: Array<DayOfMonth> = [];
      let loopMom = moment(filter.dateRange.start);
      while (loopMom.isSameOrBefore(filter.dateRange.end)) {
        retDOMArr.push(new DayOfMonth(moment(loopMom), loopMom.month() == this.curSF.month, data.filter(s => moment(s.start).isSameOrBefore(loopMom, 'day') && moment(s.end).isSameOrAfter(loopMom, 'day'))));
        loopMom.add(1, 'day');
      }

      this.showCalendar = true;

      return retDOMArr;
    })
  );

  //***************************
  // Both
  //***************************

  handleDetailLinkClick(sr: ScheduledResource) {
    //Should refactor to not have to do all this translation at some point
    const gi = new GanttSchedule(sr, 0);
    this.scheduleItemToEdit = new ScheduleItem(gi);
  }

  calendarDaySelected: BehaviorSubject<Array<DayOfMonth>> = new BehaviorSubject(null);
  detailView: Observable<DetailView> = combineLatest([this.calendarDaySelected, this.data, this.scheduleFilter]).pipe(
    map(([selectedDay, data, filter]) => {
      let start = filter.dateRange.start;
      let end = filter.dateRange.end;
      if (selectedDay !== null){
        start = selectedDay[0].day;
        end = start;
      }
      let retVal: DetailView = new DetailView(start, end, (selectedDay) ? [].concat.apply([], selectedDay.map(d => d.scheduled)) : data);
      return retVal;
    })
  );

  //pass null to cancel
  updateSI(sch: SchScheduleItem) {
    this.isNewSch = undefined;
    this.scheduleItemToEdit = undefined;

    if (sch !== null) {
      this.getData(this.year, this.curSF.siteId);
    }
    else {
      //Forces reloading of existing data without db query when cancelling
      this.pushNext();
    }
  }

  addNewScheduleItem() {
    this.isNewSch = true;

    // Clear siSubject for Travel Leg data?

    let schItem: ScheduleItem = new ScheduleItem();

    //Added below for validation purposes
    schItem.scheduleItemId = 0;
    schItem.scheduleItemTypeId = null;
    schItem.siteId = null;
    let m = moment(new Date).startOf('day');
    schItem.scheduleItemStart = m.toDate();
    schItem.scheduleItemEnd = m.add(1, 'd').toDate();   // moment(new Date).endOf('day').toDate();

    this.setSchItem(schItem);
  }

  updateFilter() {
    let sd = moment(this.curSF.dateRange.start);
    //For calendar view the start will be the month before a lot of the time so we add one month here
    if (sd.month() !== this.scheduleFilter.value.month && this.curSF.selectedView == SchViews.calendar) {
      sd = sd.add(1, 'month');
    }

    //The month dropdown alters the dates differently than the normal filtering
    if (this.monthUpdated && this.curSF.month !== sd.month() && (this.curSF.selectedView == SchViews.month || this.curSF.selectedView == SchViews.threeMonths || this.curSF.selectedView == SchViews.calendar)) {
      this.curSF.updateDateRange(moment(sd).add(this.curSF.month - sd.month(), 'months'));
    }
    else {
      this.monthUpdated = false;
      this.curSF.updateDateRange(moment(sd));
    }

    if (this.curSF.dateRange.start.year() !== this.year) {
      // so the date dropdown matches the date set
      this.year = this.curSF.dateRange.start.year();
    }

    this.pushNext();
  }

  monthUpdated: boolean = false;
  updateMonth() {
   this.monthUpdated = true;
   this.updateFilter();
  }

  updateName(name: string) {
    this.curSF.name = name;
    this.nameInput.nativeElement.value = name;
    this.updateFilter();
  }

  //***************************
  // Widget Actions
  //***************************

  clickGoSI() {
    if (this.siNum !== null) {
      this.spinnerText = "Loading...";
      this.showSpinner = true;
      this.transferHttp.getSingle(`Sch/GetScheduleItem?siId=${this.siNum}`).subscribe(s => {
        this.setSchItem(new ScheduleItem(s));
        this.showSpinner = false;
      })
    }
  }

  clickGoTR() {
    if (this.trNum !== null) {
      this.router.navigate(['/scheduling/travelRequest/' + this.trNum]);
    }
  }

  getSI() {
    this.route.params.pipe(take(1)).subscribe(params => {
      if (params['siId']) {
        let siId = params['siId'];
        this.spinnerText = "Loading...";
        this.showSpinner = true;
        this.transferHttp.getSingle(`Sch/GetScheduleItem?siId=${siId}`).subscribe(s => {
          this.setSchItem(new ScheduleItem(s));
          this.showSpinner = false;
        });
        this.location.replaceState('scheduling');
      }
    });

  }

  goToToday() {
    const year: number = moment().year();
    if (this.curSF.dateRange.start.year() !== year) {
      this.getData(year, this.curSF.siteId);
      this.year = year;
    }
    this.curSF.updateDateRange(moment());
    this.pushNext();
  }

  calendarClick() {
    this.curSF = new ScheduleFilter(this.curSF.name, this.curSF.siteId, this.curSF.jobTitleId, SchViews.calendar, this.curSF.dateRange.start, this.curSF.onlySitePositions, this.curSF.onlyNonEmp, this.curSF.onlyEmp, this.curSF.onlyBilleting, this.curSF.onlyTraining, this.curSF.onlyFlights, this.curSF.onlyPending);
    this.updateFilter();
  }

  ganttClick() {
    let currDateRange = this.curSF.dateRange.start;
    let currView = this.curSF.selectedView;
    if (this.curSF.selectedView == SchViews.threeMonths) {
      this.curSF = new ScheduleFilter(this.curSF.name, this.curSF.siteId, this.curSF.jobTitleId, SchViews.threeMonths, moment(this.curSF.dateRange.start).date(1), this.curSF.onlySitePositions, this.curSF.onlyNonEmp, this.curSF.onlyEmp, this.curSF.onlyBilleting, this.curSF.onlyTraining, this.curSF.onlyFlights, this.curSF.onlyPending);
    }
    else if (currView != this.schViews.calendar) {
      this.curSF = new ScheduleFilter(this.curSF.name, this.curSF.siteId, this.curSF.jobTitleId, SchViews.month, moment(currDateRange).date(1), this.curSF.onlySitePositions, this.curSF.onlyNonEmp, this.curSF.onlyEmp, this.curSF.onlyBilleting, this.curSF.onlyTraining, this.curSF.onlyFlights, this.curSF.onlyPending);
    }
    else {
      this.curSF = new ScheduleFilter(this.curSF.name, this.curSF.siteId, this.curSF.jobTitleId, SchViews.month, moment(this.curSF.dateRange.start).add(1, 'month').date(1), this.curSF.onlySitePositions, this.curSF.onlyNonEmp, this.curSF.onlyEmp, this.curSF.onlyBilleting, this.curSF.onlyTraining, this.curSF.onlyFlights, this.curSF.onlyPending);
    }
    this.updateFilter();
  }

  clearFilter() {
    this.nameInput.nativeElement.value = null;
    this.curSF.updateDateRange(moment());
    this.curSF = new ScheduleFilter(null, this.curSF.siteId, null, this.curSF.selectedView, this.curSF.dateRange.start, false, false, false, false, false, false, false);
    this.updateFilter();
  }

  siteChange(siteId: number) {
    this.getData(this.year, siteId);
    this.updateFilter();
    this.pushNext();
  }

  yearChange(year: number) {
    this.getData(year, this.curSF.siteId);
    this.curSF.updateDateRange(moment(this.curSF.dateRange.start).add(year - this.scheduleFilter.value.dateRange.start.year(), 'years'));
    this.pushNext();
  }

  getData(year: number, siteId: number) {
    this.showSch = false;
    this.schParams.next([new HubParam("year", year), new HubParam("siteId", siteId)]);
  }

  pushNext() {
    this.calendarDaySelected.next(null);
    this.scheduleFilter.next(Object.assign({}, this.curSF));
  }

  //***************************
  // Init
  //***************************

  ngOnInit() {
    this.getSI();
  }

  ngAfterViewInit() {

    fromEvent(this.nameInput.nativeElement, 'keyup').pipe(
      map((event: any) => event.target.value),
      debounceTime(1000),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    )
      .subscribe((value: string) => {
        this.curSF.name = value;
        this.updateFilter();
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
